import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, Mail, User, ArrowRight, Loader2, AlertCircle, Shield, CheckCircle } from 'lucide-react';
import logoUrbnet from '@/assets/images/UrbNet2LOGO.png';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { autenticacaoService } from '@/services/autenticacaoService';
import { useToast } from '@/hooks/use-toast';

export default function CriarContaPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    nome: '',
    email: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formData.nome.trim().length < 3) {
      newErrors.nome = 'Nome deve ter pelo menos 3 caracteres';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!formData.email.includes('@')) {
      newErrors.email = 'E-mail inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Usar o serviço de autenticação real para criar usuário
      const criarUsuarioRequest = {
        id: formData.email,
        nome: formData.nome
      };

      const resultado = await autenticacaoService.criarUsuario(criarUsuarioRequest);

      if ('success' in resultado) {
        // Salvar token e redirecionar para tela de código de autenticação
        await autenticacaoService.salvarToken(resultado.success);

        toast({
          title: 'Conta criada com sucesso!',
          description: `Bem-vindo, ${resultado.success.usuario.nome}!`,
        });
        
        // Salvar o GUID do usuário para usar nas próximas etapas
        const userId = resultado.success.usuario?.id || resultado.success.usuario?.['id'];
        if (!userId) {
          toast({
            title: 'Erro ao criar conta',
            variant: 'destructive',
          });
          return;
        }
        localStorage.setItem('userId', userId);
        localStorage.setItem('userEmail', formData.email);
        
        // Redirecionar para tela de código de autenticação com o GUID e email do usuário
        setTimeout(() => {
          navigate(`/autenticacao-dois-fatores?id=${userId}&email=${formData.email}`);
        }, 1500);
      } else {
        // Erro na criação
        toast({
          title: 'Erro ao criar conta',
          description: resultado.failure.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Erro ao criar conta',
        description: 'Ocorreu um erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-dark via-primary to-secondary relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-secondary/20 rounded-full blur-2xl" />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTAgMGg0MHY0MEgweiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-16 object-contain"
            />
          </div>

          {/* Main Content */}
          <div className="space-y-8 max-w-md">
            <div>
              <h2 className="font-heading text-4xl font-bold leading-tight">
                Crie sua conta
                <br />
                em minutos
              </h2>
              <p className="mt-6 text-lg text-white/80 leading-relaxed">
                Junte-se a centenas de ouvidorias que já transformam 
                a gestão de denúncias urbanas com nossa plataforma.
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-300" />
                <span className="text-white/90">Dashboard completo e intuitivo</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-300" />
                <span className="text-white/90">Análises em tempo real</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-300" />
                <span className="text-white/90">Integração total com sistemas existentes</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-300" />
                <span className="text-white/90">Suporte técnico especializado</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-sm text-white/50">
            <p>© 2024 UrbNet. Todos os direitos reservados.</p>
            <div className="flex gap-4">
              <Link to="/termos-uso" className="hover:text-white/70 transition-colors">Termos</Link>
              <Link to="/politica-privacidade" className="hover:text-white/70 transition-colors">Privacidade</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Registration Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-14 object-contain"
            />
          </div>

          {/* Header */}
          <div className="text-center lg:text-left">
            <h2 className="font-heading text-3xl font-bold">
              Criar Conta
            </h2>
            <p className="text-muted-foreground mt-2">
              Preencha seus dados básicos para começar
            </p>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="nome" className="text-sm font-medium">
                Nome Completo
              </Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="nome"
                  type="text"
                  placeholder="João Silva"
                  value={formData.nome}
                  onChange={handleInputChange('nome')}
                  className={cn(
                    'pl-12 h-14 text-base bg-surface-container border-border/50',
                    'focus:bg-background focus:border-primary transition-colors',
                    errors.nome && 'border-destructive'
                  )}
                  disabled={isLoading}
                  autoComplete="name"
                />
              </div>
              {errors.nome && (
                <p className="text-sm text-destructive">{errors.nome}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                E-mail
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.gov.br"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  className={cn(
                    'pl-12 h-14 text-base bg-surface-container border-border/50',
                    'focus:bg-background focus:border-primary transition-colors',
                    errors.email && 'border-destructive'
                  )}
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            {/* Error Message */}
            {Object.values(errors).some(error => error) && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive animate-scale-in">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">Verifique os campos destacados</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading || !formData.nome || !formData.email}
              className="w-full h-14 text-base font-medium gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Criando conta...
                </>
              ) : (
                <>
                  Criar Conta
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </Button>
          </form>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Shield className="h-4 w-4" />
            <span>Conexão segura com criptografia de ponta a ponta</span>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-primary hover:underline font-medium"
            >
              Já tem uma conta? Faça login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
